import type { HomeHeroFullBgData } from "@/lib/homepage";

type HeroWFullBGProps = {
  data?: HomeHeroFullBgData;
};

const DEFAULT_DATA: Required<HomeHeroFullBgData> = {
  heading: "Header Title",
  description: "This is the description",
  ctaLabel: "Let's get started",
  ctaUrl: "#",
  contained: false,
  backgroundImageMobile:
    "https://api.builder.io/api/v1/image/assets/TEMP/1b8400079acc2563b33c485a6d36940c0c4734a2?width=750",
  backgroundImageSmallTablet:
    "https://api.builder.io/api/v1/image/assets/TEMP/1c4fdacc8948d0b829ba180f3571d3a862b4bdaa?width=1200",
  backgroundImageTablet:
    "https://api.builder.io/api/v1/image/assets/TEMP/1c4fdacc8948d0b829ba180f3571d3a862b4bdaa?width=1200",
  backgroundImageSmallDesktop:
    "https://api.builder.io/api/v1/image/assets/TEMP/cf3e9d42c9ec2f2f61e7987c1a4917516153f970?width=1536",
  backgroundImageDesktop:
    "https://api.builder.io/api/v1/image/assets/TEMP/cf3e9d42c9ec2f2f61e7987c1a4917516153f970?width=1536",
};

export default function HeroWFullBG({ data }: HeroWFullBGProps) {
  const resolved = {
    heading: data?.heading || DEFAULT_DATA.heading,
    description: data?.description || DEFAULT_DATA.description,
    ctaLabel: data?.ctaLabel || DEFAULT_DATA.ctaLabel,
    ctaUrl: data?.ctaUrl || DEFAULT_DATA.ctaUrl,
    contained: data?.contained ?? DEFAULT_DATA.contained,
    backgroundImageMobile: data?.backgroundImageMobile || DEFAULT_DATA.backgroundImageMobile,
    backgroundImageSmallTablet:
      data?.backgroundImageSmallTablet || DEFAULT_DATA.backgroundImageSmallTablet,
    backgroundImageTablet: data?.backgroundImageTablet || DEFAULT_DATA.backgroundImageTablet,
    backgroundImageSmallDesktop:
      data?.backgroundImageSmallDesktop || DEFAULT_DATA.backgroundImageSmallDesktop,
    backgroundImageDesktop: data?.backgroundImageDesktop || DEFAULT_DATA.backgroundImageDesktop,
  } as const;

  const bgMobile = resolved.backgroundImageMobile;
  const bgSmallTablet = resolved.backgroundImageSmallTablet || bgMobile;
  const bgDesktop =
    resolved.backgroundImageDesktop ||
    resolved.backgroundImageSmallDesktop ||
    resolved.backgroundImageTablet ||
    bgSmallTablet;

  return (
    <section
      className={`relative min-h-[569px] sm:min-h-[495px] md:min-h-[447px] flex flex-col items-center justify-center px-6 pt-48 pb-24 overflow-hidden ${resolved.contained ? "max-w-[1152px] mx-auto" : "w-full"}`}
    >
      {/* Background Images - Responsive */}
      <div className="absolute inset-0 bg-cover bg-center bg-no-repeat">
        {/* Mobile (up to 639px) */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat sm:hidden"
          style={{
            backgroundImage: bgMobile ? `url('${bgMobile}')` : undefined,
          }}
        />
        {/* Tablet (640px to 767px) */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat hidden sm:block md:hidden"
          style={{
            backgroundImage: bgSmallTablet ? `url('${bgSmallTablet}')` : undefined,
          }}
        />
        {/* Desktop (768px+) */}
        <div
          className="absolute inset-0 bg-contain bg-center bg-no-repeat hidden md:block"
          style={{
            backgroundImage: bgDesktop ? `url('${bgDesktop}')` : undefined,
          }}
        />
      </div>

      {/* Overlay for better text readability */}
      <div className="absolute inset-0" />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center text-center max-w-4xl mx-auto space-y-16 md:space-y-24">
        {/* Title and Description */}
        <div className="space-y-4">
          <h1 className="text-white text-[42px] leading-[48px] font-normal">
            {resolved.heading}
          </h1>
          <div className="max-w-[400px] mx-auto">
            <p className="text-white/70 text-lg leading-[26px] font-normal">
              {resolved.description}
            </p>
          </div>
        </div>

        {/* CTA Button */}
        {resolved.ctaUrl ? (
          <a
            href={resolved.ctaUrl}
            className="flex h-[45px] px-6 justify-center items-center gap-1.5 bg-onplexus-gold text-onplexus-dark hover:bg-onplexus-gold-bright rounded-xl pl-6 pr-4 py-3 h-12 font-semibold text-lg cursor-pointer no-underline"
          >
            <span>{resolved.ctaLabel}</span>
            <span className="onplexus-icon text-5xl pt-0.5">→</span>
          </a>
        ) : null}
      </div>
    </section>
  );
}
